import { initReveal } from '../common/reveal';
import { cultureHeaders, getCurrentCulture, localizedPath, projectPath } from '../common/localizedRoutes';

interface ProjectDto {
    id: string;
    slug: string;
    category: string;
    client: string;
    year: number;
    impactScore: number;
    displayOrder: number;
    heroImageUrl?: string | null;
    coverVideoUrl?: string | null;
    tags: string[];
    techStack: string[];
    title: string;
    summary: string;
    problem: string;
    goals: string;
    architecture: string;
    duration: string;
    teamSize: string;
    solutionHighlights: string[];
    results: string[];
    keyMetrics: ProjectKeyMetricDto[];
    links: ProjectLinkDto[];
    timeline: ProjectTimelineEntryDto[];
    gallery: ProjectGalleryItemDto[];
    createdAt?: string;
    updatedAt?: string;
}

interface ProjectListResponse {
    page: number;
    pageSize: number;
    totalCount: number;
    categories: string[];
    projects: ProjectDto[];
}

interface ProjectKeyMetricDto {
    label: string;
    value: string;
}

interface ProjectLinkDto {
    label: string;
    url: string;
}

interface ProjectTimelineEntryDto {
    when: string;
    what: string;
}

interface ProjectGalleryItemDto {
    src: string;
    alt?: string | null;
}

const ALL_CATEGORY_KEY = '__all__';
const GRADIENT_CLASSES = ['g1', 'g2', 'g3', 'g4', 'g5', 'g6', 'g7'];

const formatMetaLine = (client: string, year: number): string => {
    if (client && year) {
        return `${client} • ${year}`;
    }

    if (client) {
        return client;
    }

    return year ? year.toString() : '';
};

const resolveServicePath = (key: string): string => {
    const map: Record<string, string> = {
        'system-design': '/competencies?focus=design-architecture',
        'start-up-support': '/competencies?focus=strategic-build',
        'ai-ml': '/competencies?focus=ai-ml-engineer',
        'devops': '/competencies?focus=devops-engineer',
        'cloud': '/competencies?focus=cloud-engineer',
        'security': '/competencies?focus=security-engineer',
        'automations': '/competencies?focus=automation-engineer',
        'consulting': '/competencies?focus=strategic-build',
        'mentoring': '/mentoring',
        'development': '/competencies?focus=software-engineer'
    };
    return localizedPath(map[key] ?? '/competencies?focus=design-architecture');
};

const renderProjects = (
    projects: ProjectDto[],
    container: HTMLElement,
    emptyText: string,
    root: HTMLElement,
    serviceLabel: string,
    ctaLabel: string
): void => {
    if (!projects.length) {
        container.innerHTML = `<p class="muted empty-state">${emptyText}</p>`;
        return;
    }

    const fragment = document.createDocumentFragment();

    projects.forEach((project, index) => {
        const card = document.createElement('article');
        card.className = 'prj-card';
        card.setAttribute('data-reveal', '');
        card.dataset.category = project.category;
        card.dataset.slug = project.slug;

        const link = document.createElement('a');
        link.className = 'prj-link';
        link.href = projectPath(project.slug);
        link.setAttribute('aria-label', project.title ?? project.slug);

        const thumb = document.createElement('div');
        const gradientClass = GRADIENT_CLASSES[index % GRADIENT_CLASSES.length];
        thumb.className = `prj-thumb ${gradientClass}`;

        if (project.heroImageUrl) {
            const img = document.createElement('img');
            img.src = project.heroImageUrl;
            img.alt = project.title ?? project.slug;
            img.loading = 'lazy';
            thumb.appendChild(img);
        }

        const overlay = document.createElement('div');
        overlay.className = 'thumb-gradient';
        thumb.appendChild(overlay);
        link.appendChild(thumb);

        const meta = document.createElement('div');
        meta.className = 'prj-meta';

        const badge = document.createElement('span');
        badge.className = 'badge';
        badge.textContent = project.category;
        meta.appendChild(badge);

        const title = document.createElement('h3');
        title.className = 'prj-title';
        title.textContent = project.title || project.slug;
        meta.appendChild(title);

        if (project.summary) {
            const summary = document.createElement('p');
            summary.className = 'prj-desc';
            summary.textContent = project.summary;
            meta.appendChild(summary);
        }

        const serviceTags = project.tags.filter(tag => tag.startsWith('service:'));
        if (serviceTags.length) {
            const serviceWrap = document.createElement('div');
            serviceWrap.className = 'prj-service';

            const label = document.createElement('span');
            label.textContent = serviceLabel;
            serviceWrap.appendChild(label);

            serviceTags.forEach(tag => {
                const serviceKey = tag.replace('service:', '');
                const link = document.createElement('a');
                link.href = resolveServicePath(serviceKey);
                link.textContent = serviceKey.replace(/-/g, ' ');
                serviceWrap.appendChild(link);
            });

            meta.appendChild(serviceWrap);
        }

        if (project.tags.length) {
            const tagList = document.createElement('ul');
            tagList.className = 'prj-tags';
            project.tags.forEach(tag => {
                const item = document.createElement('li');
                item.textContent = tag;
                tagList.appendChild(item);
            });
            meta.appendChild(tagList);
        }

        const footerText = formatMetaLine(project.client, project.year);
        if (footerText) {
            const footer = document.createElement('p');
            footer.className = 'muted';
            footer.textContent = footerText;
            meta.appendChild(footer);
        }

        const cta = document.createElement('span');
        cta.className = 'prj-cta';
        cta.textContent = ctaLabel;
        meta.appendChild(cta);

        link.appendChild(meta);
        card.appendChild(link);
        fragment.appendChild(card);
    });

    container.replaceChildren(fragment);
    initReveal({ selector: '#prjsPage [data-reveal]', root, threshold: 0.18 });
};

const renderPagination = (
    container: HTMLElement,
    totalCount: number,
    pageSize: number,
    currentPage: number
): void => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    if (totalPages <= 1) {
        container.innerHTML = '';
        return;
    }

    const prevLabel = container.getAttribute('data-prev-label') ?? 'Prev';
    const nextLabel = container.getAttribute('data-next-label') ?? 'Next';

    const fragment = document.createDocumentFragment();

    const addButton = (label: string, page: number, disabled = false, active = false): void => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.dataset.page = page.toString();
        if (disabled) {
            button.disabled = true;
        }
        if (active) {
            button.classList.add('is-active');
        }
        fragment.appendChild(button);
    };

    addButton(prevLabel, Math.max(1, currentPage - 1), currentPage <= 1);

    const maxButtons = 5;
    const start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    for (let page = start; page <= end; page += 1) {
        addButton(page.toString(), page, false, page === currentPage);
    }

    addButton(nextLabel, Math.min(totalPages, currentPage + 1), currentPage >= totalPages);

    container.replaceChildren(fragment);
};

const initProjectsSection = (): void => {
    const root = document.getElementById('prjsPage');
    if (!root) {
        return;
    }

    const cardsWrap = root.querySelector<HTMLElement>('#prjCards');
    const tabsContainer = root.querySelector<HTMLElement>('#prjTabs');
    const searchInput = root.querySelector<HTMLInputElement>('#prjSearch');
    const sortSelect = root.querySelector<HTMLSelectElement>('#prjSort');
    const clearButton = root.querySelector<HTMLButtonElement>('#prjClearFilters');
    const pagination = root.querySelector<HTMLElement>('#prjPagination');

    if (!cardsWrap || !tabsContainer || !searchInput || !sortSelect || !pagination) {
        return;
    }

    const emptyText = cardsWrap.getAttribute('data-empty-text') ?? 'No projects available.';
    const serviceLabel = root.getAttribute('data-service-label') ?? 'Service:';
    const ctaLabel = root.getAttribute('data-cta-label') ?? 'View case study';
    const allLabel = tabsContainer.getAttribute('data-all-label') ?? 'All';
    const culture = getCurrentCulture();

    let requestToken = 0;
    let filtersInitialized = false;
    let categories: string[] = [];
    let currentCategory = ALL_CATEGORY_KEY;
    let currentSearch = '';
    let currentSort = sortSelect.value || 'recent';
    let searchTimer = 0;
    let currentPage = 1;
    const pageSize = 10;

    const setLoading = (): void => {
        cardsWrap.innerHTML = '<p class="muted">Loading…</p>';
    };

    const updateChipStates = (): void => {
        const chips = tabsContainer.querySelectorAll<HTMLButtonElement>('.chip');
        chips.forEach(chip => {
            const value = chip.dataset.filter ?? ALL_CATEGORY_KEY;
            const isActive = value === currentCategory;
            chip.classList.toggle('is-active', isActive);
            chip.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
    };

    const handleChipSelection = (value: string): void => {
        if (value === currentCategory) {
            return;
        }

        currentCategory = value;
        currentPage = 1;
        updateChipStates();
        void loadProjects();
    };

    const renderCategoryChips = (): void => {
        tabsContainer.innerHTML = '';
        tabsContainer.setAttribute('role', 'tablist');

        const options = [
            { value: ALL_CATEGORY_KEY, label: allLabel },
            ...categories.map(label => ({ value: label, label }))
        ];

        options.forEach(({ value, label }) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'chip';
            button.dataset.filter = value;
            button.textContent = label;
            button.setAttribute('role', 'tab');
            button.setAttribute('aria-selected', value === currentCategory ? 'true' : 'false');
            button.addEventListener('click', () => handleChipSelection(value));
            tabsContainer.appendChild(button);
        });

        updateChipStates();
    };

    const loadProjects = async (): Promise<void> => {
        const token = ++requestToken;
        setLoading();

        const params = new URLSearchParams();
        params.set('page', currentPage.toString());
        params.set('pageSize', pageSize.toString());
        if (currentCategory !== ALL_CATEGORY_KEY) {
            params.set('category', currentCategory);
        }
        if (currentSearch) {
            params.set('search', currentSearch);
        }
        if (currentSort) {
            params.set('sort', currentSort);
        }

        try {
            const response = await fetch(`/api/catalog/projects${params.toString() ? `?${params.toString()}` : ''}`, {
                headers: cultureHeaders(culture)
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const data = await response.json() as ProjectListResponse;
            if (token !== requestToken) {
                return;
            }

            if (!filtersInitialized) {
                const set = new Set<string>(data.categories ?? []);
                if (!set.size) {
                    data.projects.forEach(project => set.add(project.category));
                }
                categories = Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                filtersInitialized = true;
                renderCategoryChips();
            }

            renderProjects(data.projects, cardsWrap, emptyText, root, serviceLabel, ctaLabel);
            updateChipStates();
            renderPagination(pagination, data.totalCount, data.pageSize, data.page);
        } catch (error) {
            if (token !== requestToken) {
                return;
            }

            console.error('Failed to load projects', error);
            cardsWrap.innerHTML = `<p class="muted empty-state">${emptyText}</p>`;
            pagination.innerHTML = '';
        }
    };

    renderCategoryChips();
    sortSelect.addEventListener('change', () => {
        currentSort = sortSelect.value || 'recent';
        currentPage = 1;
        void loadProjects();
    });

    searchInput.addEventListener('input', () => {
        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
            currentSearch = searchInput.value.trim();
            currentPage = 1;
            void loadProjects();
        }, 250);
    });

    clearButton?.addEventListener('click', () => {
        currentCategory = ALL_CATEGORY_KEY;
        currentSearch = '';
        currentSort = 'recent';
        currentPage = 1;
        searchInput.value = '';
        sortSelect.value = currentSort;
        updateChipStates();
        void loadProjects();
    });

    pagination.addEventListener('click', event => {
        const target = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('button[data-page]');
        if (!target) {
            return;
        }

        const pageValue = Number(target.dataset.page ?? '1');
        if (Number.isNaN(pageValue) || pageValue === currentPage) {
            return;
        }

        currentPage = pageValue;
        void loadProjects();
    });

    void loadProjects();
};

initReveal({ selector: '#prjsPage [data-reveal]', threshold: 0.18 });
initReveal({ selector: '#craftPage [data-reveal]', threshold: 0.18 });
initProjectsSection();
